import { FormControlLabel, RadioGroup as MuiRadioGroup, Radio, Typography } from '@mui/material';
import { useCallback, type ChangeEvent, type ReactNode } from 'react';

export type OptionCompareFn<Option extends string> = (optionValue: Option, selectedValue?: Option) => boolean;

export type OptionLabelPair<Option extends string> = {
  value: Option;
  label: string;
  renderAddon?: () => ReactNode;
};

type RadioGroupProps<Option extends string> = {
  title: string;
  value?: Option;
  options: OptionLabelPair<Option>[];
  onChange: (selectedOption: Option) => void;
  disabled?: boolean;
  keyExtractor: (option: OptionLabelPair<Option>) => string;
  compareFunction?: OptionCompareFn<Option>;
};

export const RadioGroup = <Option extends string>({
  title,
  value,
  options,
  onChange,
  keyExtractor,
  disabled = false,
  compareFunction = (option, selectedOption) => option === selectedOption,
}: RadioGroupProps<Option>) => {
  const disabledTypographyStyle = disabled ? { opacity: 0.6 } : {};

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.defaultValue as Option);
    },
    [onChange],
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      <Typography variant="subtitle2" style={disabledTypographyStyle} sx={{
        fontWeight: 600
      }}>
        {title}
      </Typography>
      <MuiRadioGroup onChange={handleChange}>
        {options.map((option) => (
          <div key={keyExtractor(option)} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <FormControlLabel
              disabled={disabled}
              control={<Radio size="small" />}
              label={
                <Typography variant="body2" style={disabledTypographyStyle}>
                  {option.label}
                </Typography>
              }
              value={option.value}
              checked={compareFunction(option.value, value)}
              sx={{
                alignItems: 'start',
                marginTop: '8px',
                marginRight: 0,
                marginBottom: '1px',
                '& .MuiRadio-root': {
                  paddingTop: 0,
                },
              }}
            />

            {option.renderAddon?.()}
          </div>
        ))}
      </MuiRadioGroup>
    </div>
  );
};
